import React, { useState, useEffect, useMemo } from 'react'
import { Text } from '../../constants/TextConstants'
import { Pagination } from '../Pagination'
import { Toast } from '../Toast'
import { epagAPI } from '../../API/epagAPI'
import { ErrorText } from '../EditProfile/Inputs/styles'
import { useIfMountedEffect } from '../../utils/utils'
import { PERMISSION_CODES } from '../../constants/permissions'
import { ROLES } from '../../constants/constants'
import { useSave } from '../../Context/saveContext'
import {
  HeaderWrapper,
  TableTitleLeft,
  InfoRowWrapper,
  InfoRow,
  UserListWrapper,
  AdvancedSearchButton,
  StyledBqSelect,
  SpinnerWrapper,
  NameInfoRowField,
  InfoRowInToolTip,
  TableWrapper
} from './styles'
import { BqOption, BqSpinner, BqTooltip, BqIcon, BqTag } from '@beeq/react'
import { NoDataFound } from '../Profiles/NoDataFound'
import RolesPermissionsTabs from '../RolesPermissionsTabs'
import UsersAdvancedFilters from './UsersAdvancedFilters'
import UsersAdvancedFiltersSelectedFilters from './UsersAdvancedFiltersSelectedFilters'
import { useBoolean } from '../../hooks/useBoolean'
import GlobalSearch from '../GlobalSearch'
import SortButton from '../SortButton'
import { useSort } from '../../hooks/useSort'
import { LoadingSpin } from '../LoadingSpin'
import PageActionWrapper from '../PageActionWrapper'
import NestedSelect from './NestedSelect'
import ClickAway from '../ClickAway'
import { getSelectedOptions, deflate, inflate } from './NestedSelect/utils'
import { RowsWrapper } from '../Profiles/styles'

const optionsList = ['deliveryUnits', 'grades', 'disciplines']

const MockListUsers = [
  {
    name: '',
    lastName: '',
    fullName: '',
    discipline: '',
    position: '',
    grade: '',
    DU: '',
    userRole: '',
    roleAccessLimits: {
      deliveryUnits: [],
      disciplines: []
    }
  }
]

const resetFilters = {
  DU: [],
  grade: [],
  discipline: [],
  userRole: [],
  locationPermission: [],
  disciplinePermission: []
}

const initialFilters = {
  name: '',
  ...resetFilters
}

export const Users = () => {
  const [isOpenUsersAdvancedFilters, { on: openUsersAdvancedFilters, off: closeUsersAdvancedFilters }] = useBoolean()
  const [options, setOptions] = useState({})
  const [userListFiltered, setUserListFiltered] = useState(MockListUsers)
  const [isLoading, setIsLoading] = useState(false)
  const [errorFetch, setErrorFetch] = useState('')
  const [actualPage, setActualPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [roleOptions, setRoleOptions] = useState([])
  const [filterStatus, setFilterStatus] = useState(initialFilters)
  const [sortState, toggleSort] = useSort({ name: { order: 'asc' } })
  const [roleChanged, { on: openRoleChangedToast, off: closeRoleChangedToast }] = useBoolean(false)

  const { userProfile, userPermissions, updateProfile, isLoading: isUseSaveLoading } = useSave()

  const resetAdvancedSearchFilterData = () => {
    setFilterStatus(initialFilters)
    handleSearch(initialFilters)
  }

  useIfMountedEffect(() => {
    if (userProfile) {
      setIsLoading(true)
      getProfiles(true, filterStatus)
    }
  }, [userProfile, actualPage, itemsPerPage, sortState])

  useEffect(() => {
    getFilterOptions()
  }, [])

  const isRoleSelectDisabled = (item, userProfile, userPermissions) => {
    const { spinStatus } = item
    const isAdminRoleMismatch =
      (userProfile?.userRole !== ROLES.admin && item.userRole === ROLES.admin) ||
      (userProfile.email === item.email && item.userRole !== ROLES.admin)
    const hasAssignRolesPermission = userPermissions.includes(PERMISSION_CODES.assignRoles.code)

    return spinStatus || isAdminRoleMismatch || !hasAssignRolesPermission
  }

  const addSpinProperty = (list) => {
    return list.map((item) => {
      const getFullName = (iname, ilastname) => iname.concat(' ').concat(ilastname)
      const userCompleted = {
        ...item,
        name: item.name,
        lastName: item.lastName,
        fullName: getFullName(item.name, item.lastName),
        discipline: item.discipline,
        grade: item.grade,
        DU: item.DU,
        roleAccessLimits: item.roleAccessLimits
      }
      return { ...userCompleted, spinStatus: false }
    })
  }

  const getUserRoles = () => {
    epagAPI
      .GetRoles()
      .then((result) => {
        if (result) {
          const data = result.map((el) => ({ value: el.name, label: el.name }))
          setRoleOptions(
            userProfile?.userRole !== ROLES.admin ? data.filter((item) => item.value !== ROLES.admin) : data
          )
        }
      })
      .catch((error) => {
        throw error
      })
  }

  const getFilterOptions = () => {
    setIsLoading(true)
    Promise.all([epagAPI.GetOptions(optionsList), epagAPI.GetDisciplines(), epagAPI.GetRegions(), epagAPI.GetRoles()])
      .then(([{ deliveryUnits, grades, disciplines }, disciplinePermissions, locationPermissions, userRoles]) => {
        setOptions({
          deliveryUnits,
          grades,
          disciplines,
          userRoles: userRoles.map((item) => item.name),
          disciplinePermissions: [
            {
              value: 'All Disciplines',
              innerValues: disciplinePermissions
                .sort()
                .filter((item) => item !== 'Project Delivery')
                .map((el) => ({ value: el }))
            }
          ],
          locationPermissions: locationPermissions.map((region) => ({
            value: region.name,
            innerValues: region.countries.map((country) => ({
              value: country.name,
              innerValues: country.DU.map((du) => ({ value: du }))
            }))
          }))
        })
      })
      .catch((error) => {
        setErrorFetch(error.message)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const getProfiles = (fetchUserRoles, filters = {}) => {
    setIsLoading(true)
    const requestFilters = filtersToRequest(filters)

    epagAPI
      .GetProfiles(
        actualPage,
        itemsPerPage,
        Object.keys(sortState).length === 0 ? { name: { order: 'asc' } } : sortState,
        requestFilters
      )
      .then((response) => {
        if (fetchUserRoles) getUserRoles()
        setUserListFiltered(addSpinProperty(response.paginatedResults))
        setIsLoading(false)
        setErrorFetch('')
        setTotalItems(response.totalRecords)
      })
      .catch((error) => {
        setErrorFetch(error.message)
        setIsLoading(false)
      })
  }

  const handleUserRole = (value, email) => {
    setUserListFiltered((prevState) => {
      const newState = prevState.map((item) => {
        if (item.email === email) {
          return { ...item, userRole: value, spinStatus: true }
        }
        return item
      })
      return newState
    })

    epagAPI
      .updateUserRole(value, email)
      .then(() => {
        getProfiles(false, filterStatus)
        updateProfile().then(openRoleChangedToast)
      })
      .catch((error) => {
        setErrorFetch(error.message)
      })
      .finally(() => setIsLoading(false))
  }

  const handleUserRoleAccessLimits = (value, email) => {
    setUserListFiltered((prevState) => {
      const newState = prevState.map((item) => {
        if (item.email === email) {
          return { ...item, roleAccessLimits: { ...item.roleAccessLimits, ...value }, spinStatus: true }
        }
        return item
      })
      return newState
    })

    epagAPI
      .updateRoleAccess(value, email)
      .then(() => {
        getProfiles(false, filtersToRequest(filterStatus))
        updateProfile()
      })
      .catch((error) => {
        setErrorFetch(error.message)
      })
      .finally(() => setIsLoading(false))
  }

  const addDefaultOptions = (roleAcces, options, defaultOption) => {
    if (!roleAcces) return []
    const [value, ...rest] = roleAcces.includes(defaultOption) ? roleAcces : [...roleAcces, defaultOption]
    const state = inflate(rest, options)
    return deflate(state, value, options)
  }

  const UserListGenerated = () => {
    return userListFiltered.map((item, index) => {
      const optionsLength = Object.keys(options).length
      const [disciplines, setDisciplines] = useState(
        optionsLength
          ? addDefaultOptions(item.roleAccessLimits?.disciplines, options.disciplinePermissions, item.discipline)
          : []
      )
      const [deliveryUnits, setDeliveryUnits] = useState(
        optionsLength
          ? addDefaultOptions(item.roleAccessLimits?.deliveryUnits, options.locationPermissions, item.DU)
          : []
      )
      return (
        <InfoRowWrapper key={index}>
          <NameInfoRowField $align='left' $width='15%'>
            <BqTooltip placement='top' displayOn='hover' data-testid={'full-name-' + index}>
              {item.fullName}
              <InfoRowInToolTip slot='trigger'>{item.fullName}</InfoRowInToolTip>
            </BqTooltip>
          </NameInfoRowField>
          <InfoRow $align='left' $width='7%'>
            {item.DU}
          </InfoRow>
          <InfoRow $align='left' $width='8%'>
            <BqTooltip placement='top' displayOn='hover' data-testid={'grade-' + index}>
              {item.grade}
              <InfoRowInToolTip slot='trigger'>{item.grade}</InfoRowInToolTip>
            </BqTooltip>
          </InfoRow>
          <InfoRow $align='left' $width='10%'>
            <BqTooltip placement='top' displayOn='hover' data-testid={'discipline-' + index}>
              {item.discipline}
              <InfoRowInToolTip slot='trigger'>{item.discipline}</InfoRowInToolTip>
            </BqTooltip>
          </InfoRow>
          <InfoRow $align='left' $width='20%'>
            <StyledBqSelect
              disabled={isRoleSelectDisabled(item, userProfile, userPermissions)}
              disableClear
              readonly
              name='role'
              placeholder={`${item.userRole ? item.userRole : Text.SelectRole}`}
              data-testid='users-bq-select-role'
              value={item.userRole}
              onBqSelect={(e) => {
                handleUserRole(e.detail.value, item.email)
              }}>
              {roleOptions &&
                roleOptions.map((option, index) => {
                  return (
                    <BqOption key={index} value={option.value}>
                      {option.value}
                    </BqOption>
                  )
                })}
            </StyledBqSelect>
          </InfoRow>
          <InfoRow $align='left' $width='20%'>
            <ClickAway
              onClickOutside={() => {
                const dp = getSelectedOptions(disciplines, options.disciplinePermissions)
                handleUserRoleAccessLimits(
                  { disciplines: !dp.includes(item.discipline) ? [...dp, item.discipline] : [...dp] },
                  item.email
                )
              }}>
              <NestedSelect
                name='disciplinePermission'
                label=''
                selectedLabel=''
                maxTagsVisible={1}
                value={disciplines}
                onSelect={setDisciplines}
                options={options.disciplinePermissions}
                placeholder='Select a Discipline'
                disabled={userProfile?.userRole !== ROLES.admin}
                data-testid='users-bq-select-discipline'
                defaultOption={item.discipline}
              />
            </ClickAway>
          </InfoRow>
          <InfoRow $align='left' $width='20%'>
            <ClickAway
              onClickOutside={() => {
                const dup = getSelectedOptions(deliveryUnits, options.locationPermissions)
                handleUserRoleAccessLimits(
                  { deliveryUnits: !dup.includes(item.DU) ? [...dup, item.DU] : [...dup] },
                  item.email
                )
              }}>
              <NestedSelect
                name='locationPermission'
                label=''
                selectedLabel=''
                maxTagsVisible={1}
                value={deliveryUnits}
                onSelect={setDeliveryUnits}
                options={options.locationPermissions}
                placeholder='Select a Location'
                disabled={userProfile?.userRole !== ROLES.admin}
                data-testid='users-bq-select-location'
                defaultOption={item.DU}
              />
            </ClickAway>
          </InfoRow>
        </InfoRowWrapper>
      )
    })
  }

  const filtersToRequest = (filters) =>
    Object.entries(filters).reduce((acc, [key, value]) => {
      // NOTE: we should ignore empty arrays
      if (Array.isArray(value) && value.length === 0) {
        return acc
      }

      switch (key) {
        case 'locationPermission': {
          acc[key] = getSelectedOptions(value, options.locationPermissions)
          break
        }

        case 'disciplinePermission': {
          acc[key] = getSelectedOptions(value, options.disciplinePermissions)
          break
        }

        default: {
          acc[key] = value
        }
      }

      return acc
    }, {})

  const handleSearch = (filters = filterStatus) => {
    setActualPage(1)
    getProfiles(true, filters)
  }

  const onPageSizeChangeHandler = (pageSize) => {
    setActualPage(1)
    setItemsPerPage(pageSize)
  }

  const filtersCount = useMemo(() => {
    const { name: _, ...rest } = filterStatus

    return Object.values(rest).reduce((acc, value) => {
      if (Array.isArray(value) && value.length) {
        return acc + 1
      } else if (typeof value === 'string' && value !== '') {
        return acc + 1
      }
      return acc
    }, 0)
  }, [filterStatus])

  if (isUseSaveLoading) {
    return <LoadingSpin loadingText={Text.GenericLoading} />
  }

  return (
    <UserListWrapper>
      <Toast open={roleChanged} type={'info'} message={Text.RoleChangedWarning} onBqHide={closeRoleChangedToast} />
      <RolesPermissionsTabs />
      <PageActionWrapper>
        <GlobalSearch
          globalSearchParam={filterStatus.name}
          placeholder={Text.UsersGlobalSearch}
          handleSearch={(name) => {
            const filters = { ...filterStatus, name }
            setFilterStatus(filters)
            handleSearch(filters)
          }}
        />
        <AdvancedSearchButton
          data-testid='advanced-search-filter-profile-button'
          appearance='primary'
          variant='ghost'
          border='s'
          size='medium'
          type='button'
          onBqClick={openUsersAdvancedFilters}>
          <BqIcon name='funnel' slot='prefix' />
          {Text.AdvancedSearchButton}
          <BqTag clickable size='small' slot='suffix'>
            {filtersCount}
          </BqTag>
        </AdvancedSearchButton>
        <UsersAdvancedFilters
          open={isOpenUsersAdvancedFilters}
          onClose={closeUsersAdvancedFilters}
          options={options}
          initialValues={filterStatus}
          resetValues={resetFilters}
          onFilter={(values) => {
            const filters = { ...values, name: filterStatus.name }
            setFilterStatus(filters)
            handleSearch(filters)
            closeUsersAdvancedFilters()
          }}
        />
      </PageActionWrapper>
      {filtersCount > 0 && (
        <div style={{ marginTop: 'var(--bq-spacing-l)' }}>
          <UsersAdvancedFiltersSelectedFilters
            values={filterStatus}
            onClose={(key, value) => {
              const filters = { ...filterStatus, [key]: value }
              setFilterStatus(filters)
              handleSearch(filters)
            }}
            orientation='horizontal'
          />
        </div>
      )}
      {!userListFiltered.length && <NoDataFound resetAdvancedSearchFilterData={resetAdvancedSearchFilterData} />}
      {!!userListFiltered.length && (
        <>
        <TableWrapper>
          <HeaderWrapper>
            <TableTitleLeft $width='15%'>
              {Text.Name} <SortButton name='name' sortOrder={sortState?.name} toggleSort={toggleSort} />
            </TableTitleLeft>
            <TableTitleLeft $width='7%'>
              {Text.DU} <SortButton name='DU' sortOrder={sortState?.DU} toggleSort={toggleSort} />
            </TableTitleLeft>
            <TableTitleLeft $width='7.5%'>
              {Text.Grade} <SortButton name='grade' sortOrder={sortState?.grade} toggleSort={toggleSort} />
            </TableTitleLeft>
            <TableTitleLeft $width='10%'>
              {Text.Discipline}{' '}
              <SortButton name='discipline' sortOrder={sortState?.discipline} toggleSort={toggleSort} />
            </TableTitleLeft>
            <TableTitleLeft $width='19.5%'>
              {Text.Role} <SortButton name='userRole' sortOrder={sortState?.userRole} toggleSort={toggleSort} />
            </TableTitleLeft>
            <TableTitleLeft $width='20%'>{Text.DisciplinePermission}</TableTitleLeft>
            <TableTitleLeft $width='20%'>{Text.LocationPermission}</TableTitleLeft>
          </HeaderWrapper>
          {isLoading ? (
            <SpinnerWrapper>
              <BqSpinner size='large' text-position='bellow' style={{ top: '100px' }} data-testid='spinner-profile'>
                <span>{Text.GenericLoading}</span>
              </BqSpinner>
            </SpinnerWrapper>
          ) : (
            <>
              <RowsWrapper>
                <UserListGenerated />
              </RowsWrapper>
            </>
          )}
          </TableWrapper>
          <Pagination
            currentPage={actualPage}
            totalItems={totalItems}
            defaultPageSize={itemsPerPage}
            onPageSizeChange={onPageSizeChangeHandler}
            onPageChange={setActualPage}
            pageSizeControlSuffix={Text.Users}
          />
        </>
      )}
      {errorFetch !== '' && <ErrorText>{errorFetch}</ErrorText>}
    </UserListWrapper>
  )
}
